import { Suspense, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, Outlet, Route, Routes } from "react-router";
import { useTypedParams } from "react-router-typesafe-routes";
import { toast } from "sonner";
import { PageLoading } from "~/components/loading";
import { v2Route } from "~/v2/routes";
import { BatchChangeRequestsJobDetailPanel } from "./components/BatchChangeRequestsJobDetailPanel";
import { BatchEvidencesJobDetailPanel } from "./components/BatchEvidencesJobDetailPanel";
import { DataImportProjectListContainer } from "./components/DataImportProjectList.container";
import { DataImportProjectPipelineContainer } from "./components/DataImportProjectPipeline.container";
import { DataSourceDesignerPage } from "./components/DataSourceDesignerPage";
import { DataSourceDetailPanel } from "./components/DataSourceDetailPanel";
import { EvidenceSetDetailPanel } from "./components/EvidenceSetDetailPanel";
import { DipLifecycleProvider, useDipLifecycle } from "./context";

function DataImportProjectLayout() {
  const { id } = useTypedParams(v2Route.dataImportProjects.detail);

  if (!id) {
    return <Navigate to={v2Route.dataImportProjects.list.$buildPath({})} replace />;
  }

  return (
    <DipLifecycleProvider projectId={id}>
      <Outlet />
    </DipLifecycleProvider>
  );
}

function DataSourceDesignerRouteGuard() {
  const { t } = useTranslation();
  const { projectId, isReadOnly } = useDipLifecycle();
  const redirectPath = isReadOnly ? v2Route.dataImportProjects.detail.$buildPath({ params: { id: projectId } }) : null;

  useEffect(() => {
    if (redirectPath) {
      toast.info(t("feature.dataImportProjects.v2.lifecycle.designerReadOnly"), {
        id: "dip-designer-read-only",
      });
    }
  }, [redirectPath, t]);

  return redirectPath ? <Navigate to={redirectPath} replace /> : <DataSourceDesignerPage />;
}

function ProjectDetailFallbackRedirect() {
  const { id } = useTypedParams(v2Route.dataImportProjects.detail);

  return (
    <Navigate
      to={
        id
          ? v2Route.dataImportProjects.detail.$buildPath({ params: { id } })
          : v2Route.dataImportProjects.list.$buildPath({})
      }
      replace
    />
  );
}

function DataSourceDetailRoute() {
  const { id, dataSourceId } = useTypedParams(v2Route.dataImportProjects.detail.dataSource);

  if (!id || !dataSourceId) {
    return <Navigate to={v2Route.dataImportProjects.list.$buildPath({})} replace />;
  }

  return <DataSourceDetailPanel importProjectId={id} dataSourceId={dataSourceId} mode="page" />;
}

function EvidenceSetDetailRoute() {
  const { id, evidenceSetId } = useTypedParams(v2Route.dataImportProjects.detail.evidenceSet);

  if (!id || !evidenceSetId) {
    return <Navigate to={v2Route.dataImportProjects.list.$buildPath({})} replace />;
  }

  return <EvidenceSetDetailPanel importProjectId={id} evidenceSetId={evidenceSetId} mode="page" />;
}

function BatchEvidencesJobDetailRoute() {
  const { jobId } = useTypedParams(v2Route.dataImportProjects.detail.batchEvidencesJob);

  if (!jobId) {
    return <Navigate to={v2Route.dataImportProjects.list.$buildPath({})} replace />;
  }

  return <BatchEvidencesJobDetailPanel jobId={jobId} mode="page" />;
}

function BatchChangeRequestsJobDetailRoute() {
  const { jobId } = useTypedParams(v2Route.dataImportProjects.detail.batchChangeRequestsJob);

  if (!jobId) {
    return <Navigate to={v2Route.dataImportProjects.list.$buildPath({})} replace />;
  }

  return <BatchChangeRequestsJobDetailPanel jobId={jobId} mode="page" initialTab="changeRequests" />;
}

export function DataImportProjectsRoutes() {
  return (
    <Suspense fallback={<PageLoading />}>
      <Routes>
        <Route index element={<DataImportProjectListContainer />} />
        <Route path=":id" element={<DataImportProjectLayout />}>
          <Route index element={<DataImportProjectPipelineContainer />} />
          <Route path="data-sources/designer" element={<DataSourceDesignerRouteGuard />} />
          <Route path="data-sources/:dataSourceId" element={<DataSourceDetailRoute />} />
          <Route path="evidence-sets/:evidenceSetId" element={<EvidenceSetDetailRoute />} />
          <Route path="batch-evidences-jobs/:jobId" element={<BatchEvidencesJobDetailRoute />} />
          <Route path="batch-change-request-jobs/:jobId" element={<BatchChangeRequestsJobDetailRoute />} />
          <Route path="data-import-pipeline-jobs/runs/:jobRunId" element={<ProjectDetailFallbackRedirect />} />
        </Route>
      </Routes>
    </Suspense>
  );
}
